import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class RouterLinkFactory {
  /**
   * Navigate application to homepage.
   *
   * Homepage is main page (component), which is rendered after login.
   * It is always safe to redirect to homepage.
   */
  homepage(): string[] {
    return ['/dashboard'];
  }

  /**
   * Navigate application to profile.
   */
  profile(): string[] {
    return ['/profile'];
  }

  /**
   * Navigate application to notification.
   */
  notification(): string[] {
    return ['/dashboard/notification'];
  }

  /**
   * Navigate application to companies.
   */
  companies(): string[] {
    return ['/dashboard/companies'];
  }

  /**
   * Navigate application to blocked company registrations.
   */
  blockedCompanyRegistrations(): string[] {
    return ['/dashboard/companies/blocked-registrations'];
  }

  /**
   * Navigate application to add company.
   */
  addCompany(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to company details.
   */
  companyDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to company offers.
   */
  companyOffers(): string[] {
    return ['/dashboard/company-offers'];
  }

  /**
   * Navigate application to add company offers.
   */
  addCompanyOffers(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to company offers details.
   */
  companyOffersDetails(id: string): string[] {
    return ['/dashboard/company-offers/details', id];
  }

  /**
   * Navigate application to company offers benf.
   */
  companyOffersBenf(id: string): string[] {
    return ['/dashboard/company-offers/benf', id];
  }

  /**
   * Navigate application to company offers rates.
   */
  companyOffersRates(id: string): string[] {
    return ['/dashboard/company-offers/rates', id];
  }

  /**
   * Navigate application to company offers rates.
   */
  editCompanyOffers(id: string): string[] {
    return ['/dashboard/company-offers/edit', id];
  }

  /**
   * Navigate application to company offers rates.
   */
  companyOffersBenfOrders(id: string): string[] {
    return ['/dashboard/company-offers/benf-orders', id];
  }

  /**
   * Navigate application to settings/cities.
   */
  settingsCities(): string[] {
    return ['/dashboard/settings/cities'];
  }

  /**
   * Navigate application to settings/offer-categories.
   */
  settingsOfferCategories(): string[] {
    return ['/dashboard/settings/offer-categories'];
  }

  /**
   * Navigate application to settings/pages.
   */
  settingsPages(): string[] {
    return ['/dashboard/settings/pages'];
  }

  /**
   * Navigate application to settings/add pages.
   */
  settingsAddPages(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to settings/edit pages.
   */
  settingsEditPages(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to settings/email.
   */
  settingsEmail(): string[] {
    return ['/dashboard/settings/email'];
  }

  /**
   * Navigate application to messages.
   */
  messages(): string[] {
    return ['/dashboard/messages'];
  }

  /**
   * Navigate application to messages details.
   */
  messagesDetails(id: string): string[] {
    return ['/dashboard/messages', id];
  }

  /**
   * Navigate application to notification settings.
   */
  notificationSettings(): string[] {
    return ['/dashboard/notification-settings'];
  }

  /**
   * Navigate application to users page.
   */
  users(): string[] {
    return ['/dashboard/users'];
  }

  /**
   * Navigate application to relatives page.
   */
  relatives(): string[] {
    return ['/dashboard/relatives'];
  }

  /**
   * Navigate application to delegates page.
   */
  delegates(): string[] {
    return ['/dashboard/delegates'];
  }

  /**
   * Navigate application to users details.
   */
  usersDetails(id: string): string[] {
    return ['/dashboard/users', id];
  }

  /**
   * Navigate application to user wallet details.
   */
  usersWalletDetails(id: string): string[] {
    return ['/dashboard/users/wallet', id];
  }

  /**
   * Navigate application to user wallet details.
   */
  usersWalletReport(id: string): string[] {
    return ['/dashboard/users/wallet', id, 'report'];
  }

  /**
   * Navigate application to user wallet due invoices.
   */
  usersWalletDueInvoices(id: string): string[] {
    return ['/dashboard/users/wallet', id, 'due-invoices'];
  }

  /**
   * Navigate application to roles details.
   */
  roles(): string[] {
    return ['/dashboard/roles'];
  }

  /**
   * Navigate application to roles permissions form.
   */
  rolesPermissions(id: string): string[] {
    return ['/dashboard/roles/permissions', id];
  }

  /**
   * Navigate application to staff details.
   */
  staff(): string[] {
    return ['/dashboard/staff'];
  }

  /**
   * Navigate application to reports.
   */
  reports(): string[] {
    return ['/dashboard/reports'];
  }

  /**
   * Navigate application to news.
   */
  news(): string[] {
    return ['/dashboard/news'];
  }

  /**
   * Navigate application to add news.
   */
  addNews(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to news details.
   */
  newsDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to edit news.
   */
  editNews(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to announcements.
   */
  announcements(): string[] {
    return ['/dashboard/announcements'];
  }

  /**
   * Navigate application to add announcement.
   */
  addAnnouncement(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to announcement details.
   */
  announcementDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to edit announcement.
   */
  editAnnouncement(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to events.
   */
  events(): string[] {
    return ['/dashboard/events'];
  }

  /**
   * Navigate application to add event.
   */
  addEvent(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to event details.
   */
  eventDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to edit event.
   */
  editEvent(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to settings/wallet-settings/edit-wallet page.
   */
  settingsWalletSettingsEditWallet(): string[] {
    return ['/dashboard/settings/wallet-settings/edit-wallet'];
  }

  /**
   * Navigate application to settings/wallet-settings/wallet-cycle page.
   */
  settingsWalletSettingsWalletCycle(): string[] {
    return ['/dashboard/settings/wallet-settings/wallet-cycle'];
  }

  /**
   * Navigate application to settings/wallet-settings/financial-report page.
   */
  settingsWalletSettingsFinancialReport(): string[] {
    return ['/dashboard/settings/wallet-settings/financial-report'];
  }

  /**
   * Navigate application to settings/wallet-settings/financial-report/beneficiary-invoices/:id page.
   */
  settingsWalletSettingsFinancialReportBeneficiaryInvoices(
    id: string
  ): string[] {
    return [
      '/dashboard/settings/wallet-settings/financial-report/beneficiary-invoices',
      id,
    ];
  }

  /**
   * Navigate application to settings/wallet-settings/export-operations-report page.
   */
  settingsWalletSettingsExportOperationsReport(): string[] {
    return ['/dashboard/settings/wallet-settings/export-operations-report'];
  }

  /**
   * Navigate application to settings/wallet-settings/payment-operations-report page.
   */
  settingsWalletSettingsPaymentOperationsReport(): string[] {
    return ['/dashboard/settings/wallet-settings/payment-operations-report'];
  }

  /**
   * Navigate application to settings/wallet-settings/admin-procedures page.
   */
  settingsWalletSettingsAdminProcedures(): string[] {
    return ['/dashboard/settings/wallet-settings/admin-procedures'];
  }

  /**
   * Navigate application to settings/wallet-settings/beneficiaries-info-wallet page.
   */
  settingsWalletSettingsBeneficiariesInfoWallet(): string[] {
    return ['/dashboard/settings/wallet-settings/beneficiaries-info-wallet'];
  }

  /**
   * Navigate application to laundries/laundries page.
   */
  laundriesLaundries(): string[] {
    return ['/dashboard/laundries/laundries'];
  }
  /**
   * Navigate application to laundries/laundries/add page.
   */
  addLaundray(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to  laundries/laundries/edit pages.
   */
  editLaundry(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to  laundries/laundries/details pages.
   */
  laundriesLaundryDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to laundries/branches page.
   */
  laundriesBranches(): string[] {
    return ['/dashboard/laundries/branches'];
  }

  /**
   * Navigate application to laundries/branches/add page.
   */
  addBranch(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to  laundries/branches/edit page.
   */
  editBranch(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to laundries/collection-points page.
   */
  laundriesCollectionPoints(): string[] {
    return ['/dashboard/laundries/collection-points'];
  }

  /**
   * Navigate application to laundries/collection-points/add page.
   */
  addCollectionPoint(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to  laundries/collection-points/edit page.
   */
  editCollectionPoint(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to laundries/addition-requests page.
   */
  laundriesAdditionRequests(): string[] {
    return ['/dashboard/laundries/addition-requests'];
  }

  /**
   * Navigate application to laundries/addition-requests/details page.
   */
  laundriesAdditionRequestsDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to laundries/employees page.
   */
  laundriesEmployees(): string[] {
    return ['/dashboard/laundries/employees'];
  }

  /**
   * Navigate application to laundries/employees/add page.
   */
  addEmployee(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to  laundries/employees/edit page.
   */
  editEmployee(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to laundries/audit-history page.
   */
  laundriesAuditHistory(): string[] {
    return ['/dashboard/laundries/audit-history'];
  }

  /**
   * Navigate application to laundries/laundry-category page.
   */
  laundriesLaundryCategories(): string[] {
    return ['/dashboard/laundries/laundry-categories'];
  }

  /**
   * Navigate application to laundries/services page.
   */
  laundriesServices(): string[] {
    return ['/dashboard/laundries/services'];
  }

  /**
   * Navigate application to laundries/settings/privileges page.
   */
  laundrySettingsPrivileges(): string[] {
    return ['/dashboard/laundries/settings/privileges'];
  }

  /**
   * Navigate application to laundries/settings/order-rules page.
   */
  laundrySettingsOrderRules(): string[] {
    return ['/dashboard/laundries/settings/order-rules'];
  }

  /**
   * Navigate application to laundries/settings/order-rules/add form page.
   */
  addOrderRule(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to laundries/settings/order-rules/edit form page.
   */
  editOrderRule(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to Overview Packages page.
   */
  laundriesOverviewPackages(): string[] {
    return ['/dashboard/laundries/packages/overview'];
  }
  /**
   * Navigate application to Add Overview Packages page.
   */
  addPackages(): string[] {
    return ['add'];
  }
  /**
   * Navigate application to Edit Overview Packages page.
   */
  editPackages(id: any): string[] {
    return ['edit', id];
  }
  /**
   * Navigate application to Packages Subscriptions page.
   */
  packagesSubscriptions(): string[] {
    return ['/dashboard/laundries/packages/subscriptions'];
  }

  /**
   * Navigate application to packages/offers-increases page.
   */
  packagesOffersIncreases(): string[] {
    return ['/dashboard/laundries/packages/offers-increases'];
  }

  /**
   * Navigate application to packages/offers-increases/add page.
   */
  addOffersIncreases(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to packages/offers-increases/edit/:id page.
   */
  editOffersIncreases(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to Working Hours page.
   */
  settingsWorkingHours(): string[] {
    return ['/dashboard/laundries/settings/working-hours'];
  }

  /**
   * Navigate application to laundries/orders page.
   */
  laundriesOrders(): string[] {
    return ['/dashboard/laundries/orders'];
  }

  laundryInvoicePrint(invoiceId: string): string[] {
    return ['/print/laundries/invoices', invoiceId];
  }

  laundryOrderPrint(orderNumber: string): string[] {
    return ['/print/laundries/orders', orderNumber];
  }

  /**
   * Navigate application to laundries/bills page.
   */
  laundriesBills(): string[] {
    return ['/dashboard/laundries/bills'];
  }
  /**
   * Navigate application to laundries/reports page.
   */
  laundriesReports(): string[] {
    return ['/dashboard/laundries/reports'];
  }

  /**
   * Navigate application to loans/overviewRequsets page.
   */
  loansLoanRequestsOverview(): string[] {
    return ['/dashboard/loans/overview-loan-requests'];
  }

  /**
   * Navigate application to loans/overviewRequsets/add page.
   */
  addLoanRequest(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to loans/overviewRequsets/edit/:id page.
   */
  editLoanRequest(id: string): string[] {
    return ['/dashboard/loans/overview-loan-requests/edit', id];
  }

  /**
   * Navigate application to  loans/overviewRequsets/detail page.
   */
  loansLoanRequestsOverviewDetails(id: string): string[] {
    return ['/dashboard/loans/overview-loan-requests', 'details', id];
  }

  /**
   * Navigate application to  loans/overviewRequsets/detail/other-loan page.
   */
  loansLoanRequestsOverviewDetailsOtherLoans(loanRequestId: string): string[] {
    return ['other-loans', loanRequestId];
  }

  /**
   * Navigate application to  loans/overviewRequsets/detail/other-loan/details page.
   */
  loansLoanRequestsOverviewDetailsOtherLoansDetails(
    otherLoanId: string
  ): string[] {
    return ['details', otherLoanId];
  }

  /**
   * Navigate application to loans/ModificationExceptions page.
   */
  loansLoanRequestsModificationExceptions(): string[] {
    return ['/dashboard/loans/modification-exceptions'];
  }

  /**
   * Navigate application to loans/ModificationExceptions/details page.
   */
  loansLoanRequestsModificationExceptionsDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to loans/loanTypes page.
   */
  loansLoanManagementLoanTypes(): string[] {
    return ['/dashboard/loans/loan-types'];
  }

  /**
   * Navigate application to loans/loanTypes/add page.
   */
  addLoanTypes(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to loans/loanTypes/edit/:id page.
   */
  editLoanTypes(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to loans/loan-type/details page.
   */
  loansLoanManagementLoanTypesDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to loans/loanCategories page.
   */
  loansLoanManagementLoanCategories(): string[] {
    return ['/dashboard/loans/loan-categories'];
  }

  /**
   * Navigate application to loans/loanCategories/add page.
   */
  addLoanCategory(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to loans/loanCategories/edit/:id page.
   */
  editLoanCategory(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to loans/loan-category/details page.
   */
  loansLoanManagementLoanCategoriesDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to loans/attachments page.
   */
  loansLoanManagementAttachments(): string[] {
    return ['/dashboard/loans/attachments'];
  }

  /**
   * Navigate application to loans/deduction-entities page.
   */
  loansDeductionEntities(): string[] {
    return ['/dashboard/loans/deduction-entities'];
  }

  /**
   * Navigate application to loans/insurances page.
   */
  loansInsurances(): string[] {
    return ['/dashboard/loans/insurances'];
  }

  /**
   * Navigate application to loans/overview-committees page.
   */
  loansCommitteesOverview(): string[] {
    return ['/dashboard/loans/overview-committees'];
  }

  /**
   * Navigate application to loans/overview-committees/details page.
   */
  loansCommitteesOverviewDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to loans/committees-members page.
   */
  loansCommitteesCommitteesMembers(): string[] {
    return ['/dashboard/loans/committees-members'];
  }

  /**
   * Navigate application to loans/overviewCommittees/add page.
   */
  addCommittee(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to loans/overviewCommittees/edit/:id page.
   */
  editCommittee(id: string): string[] {
    return ['edit', id];
  }

  /**
   * Navigate application to loans/overview-committees/committee-requests page.
   */
  loansCommitteesOverviewCommitteeRequests(id: string): string[] {
    return ['committee-requests', id];
  }

  /**
   * Navigate application to loans/overview-committees/committee-requests/:id/details/:requestId page.
   */
  loansCommitteesOverviewCommitteeRequestsDetails(
    id: string,
    requestId: string
  ): string[] {
    return [
      '/dashboard/loans/overview-committees/committee-requests',
      id,
      'details',
      requestId,
    ];
  }

  /**
   * Navigate application to loans/committee-requests page.
   */
  loansCommitteesCommitteesRequests(): string[] {
    return ['/dashboard/loans/committees-requests'];
  }

  /**
   * Navigate application to loans/committee-requests/details page.
   */
  loansCommitteesCommitteesRequestsDetails(id: string): string[] {
    return ['details', id];
  }

  /**
   * Navigate application to settings/message-entites page.
   */
  settingsMessageEntites(): string[] {
    return ['/dashboard/settings/message-entites'];
  }

  /**
   * Navigate application to settings/payment-methods page.
   */
  settingsPaymentMethods(): string[] {
    return ['/dashboard/settings/payment-methods'];
  }

  /**
   * Navigate application to stores/stores page.
   */
  storesStores(): string[] {
    return ['/dashboard/stores/stores'];
  }

  /**
   * Navigate application to store details page.
   */
  storeDetails(storeId: string): string[] {
    return ['/dashboard/stores/stores/store', storeId, 'details'];
  }

  /**
   * Navigate application to add store page.
   */
  storeAdd(): string[] {
    return ['add'];
  }

  /**
   * Navigate application to edit store page.
   */
  storeEdit(storeId: string): string[] {
    return ['edit', storeId];
  }

  /**
   * Navigate application to store branches (sibling of details).
   */
  storeBranches(storeId: string): string[] {
    return ['/dashboard/stores/stores/store', storeId, 'branches'];
  }

  /**
   * Navigate application to add store branch.
   */
  storeBranchAdd(storeId: string): string[] {
    return ['/dashboard/stores/stores/store', storeId, 'branches', 'add'];
  }

  /**
   * Navigate application to edit store branch.
   */
  storeBranchEdit(storeId: string, branchId: string): string[] {
    return [
      '/dashboard/stores/stores/store',
      storeId,
      'branches',
      'edit',
      branchId,
    ];
  }

  /**
   * Navigate application to store invoices tab.
   */
  storeInvoices(storeId: string): string[] {
    return ['/dashboard/stores/stores/store', storeId, 'invoices'];
  }

  /**
   * Navigate application to internal invoice details.
   */
  storeInvoiceDetails(storeId: string, invoiceId: string): string[] {
    return [
      '/dashboard/stores/stores/store',
      storeId,
      'invoices',
      'internal',
      'details',
      invoiceId,
    ];
  }

  storeInvoicePrint(
    storeId: string,
    invoiceId: string,
    type: 'external' | 'internal'
  ): string[] {
    return ['/print/stores', storeId, 'invoices', type, invoiceId];
  }

  /**
   * Navigate application to external invoice details.
   */
  storeExternalInvoiceDetails(storeId: string, invoiceId: string): string[] {
    return [
      '/dashboard/stores/stores/store',
      storeId,
      'invoices',
      'external',
      'details',
      invoiceId,
    ];
  }

  /**
   * Navigate application to store activities list page.
   */
  storeActivitiesList(): string[] {
    return ['/dashboard/stores/store-activities'];
  }

  /**
   * Navigate application to stores/reports page.
   */
  storesReports(): string[] {
    return ['/dashboard/stores/reports'];
  }

  /**
   * Navigate application to stores/settings/return-policy page.
   */
  storesSettingsReturnPolicy(): string[] {
    return ['/dashboard/stores/settings/return-policy'];
  }
}
